import { useState } from 'react';
import { Input } from '@paulownia/shared/components/ui/input';
import { Label } from '@paulownia/shared/components/ui/label';
import { Checkbox } from '@paulownia/shared/components/ui/checkbox';
import type { WeightEstimate } from '@paulownia/shared/types';

const DEFAULT_DISCLAIMER = 'Prix final selon poids réel';

interface UnitPricedByWeightFieldsProps {
  price?: number;
  weightEstimate?: WeightEstimate;
  disclaimer?: string;
  onPriceChange: (price: number | undefined) => void;
  onWeightEstimateChange: (estimate: WeightEstimate | undefined) => void;
  onDisclaimerChange: (disclaimer: string | undefined) => void;
}

export function UnitPricedByWeightFields({
  price,
  weightEstimate,
  disclaimer,
  onPriceChange,
  onWeightEstimateChange,
  onDisclaimerChange,
}: UnitPricedByWeightFieldsProps) {
  // Local state to prevent React from overwriting user input during typing
  const [priceInput, setPriceInput] = useState(
    price ? String(price) : ''
  );
  const [weightInput, setWeightInput] = useState(
    weightEstimate?.averageWeight ? String(weightEstimate.averageWeight) : ''
  );
  const [servingsMinInput, setServingsMinInput] = useState(
    weightEstimate?.servesPerUnit?.min ? String(weightEstimate.servesPerUnit.min) : ''
  );
  const [servingsMaxInput, setServingsMaxInput] = useState(
    weightEstimate?.servesPerUnit?.max ? String(weightEstimate.servesPerUnit.max) : ''
  );

  const updateWeightEstimate = (updates: Partial<WeightEstimate>) => {
    const current = weightEstimate || { averageWeight: 0 };
    onWeightEstimateChange({ ...current, ...updates });
  };

  const updateServesPerUnit = (field: 'min' | 'max', newFieldValue: string) => {
    const current = weightEstimate || { averageWeight: 0 };

    // Get values from local state, with the new value for the changed field
    const minStr = field === 'min' ? newFieldValue : servingsMinInput;
    const maxStr = field === 'max' ? newFieldValue : servingsMaxInput;

    const minVal = minStr ? parseInt(minStr, 10) : undefined;
    const maxVal = maxStr ? parseInt(maxStr, 10) : undefined;

    // Only set servesPerUnit if both values are valid numbers
    if (minVal !== undefined && !isNaN(minVal) && maxVal !== undefined && !isNaN(maxVal)) {
      onWeightEstimateChange({
        ...current,
        servesPerUnit: { min: minVal, max: maxVal },
      });
    } else {
      // Remove servesPerUnit if either value is missing
      onWeightEstimateChange({ ...current, servesPerUnit: undefined });
    }
  };

  // Calculate estimated price per unit
  const estimatedPrice = price && weightEstimate?.averageWeight
    ? price * weightEstimate.averageWeight
    : null;

  return (
    <div className="space-y-4">
      {/* Price per kg */}
      <div className="space-y-2">
        <Label htmlFor="price-kg">Prix au kilo (€/kg) *</Label>
        <Input
          id="price-kg"
          type="text"
          inputMode="decimal"
          value={priceInput}
          onChange={(e) => {
            const val = e.target.value;
            if (val === '' || /^\d*[.,]?\d{0,2}$/.test(val)) {
              setPriceInput(val);
              const normalized = val.replace(',', '.');
              const parsed = parseFloat(normalized);
              onPriceChange(val === '' ? undefined : (isNaN(parsed) ? undefined : parsed));
            }
          }}
          placeholder="ex: 15.00"
        />
      </div>

      {/* Average weight */}
      <div className="space-y-2">
        <Label htmlFor="avg-weight">Poids moyen (kg) *</Label>
        <Input
          id="avg-weight"
          type="text"
          inputMode="decimal"
          value={weightInput}
          onChange={(e) => {
            const val = e.target.value;
            if (val === '' || /^\d*[.,]?\d{0,3}$/.test(val)) {
              setWeightInput(val);
              const normalized = val.replace(',', '.');
              const parsed = parseFloat(normalized);
              updateWeightEstimate({
                averageWeight: val === '' ? 0 : (isNaN(parsed) ? 0 : parsed),
              });
            }
          }}
          placeholder="ex: 1.2"
        />
        <p className="text-xs text-gray-500">
          Poids moyen d'une pièce pour le calcul du prix estimé.
        </p>
      </div>

      {/* Estimated price display */}
      {estimatedPrice !== null && (
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800">
            <span className="font-medium">Prix estimé par pièce :</span>{' '}
            ≈ {estimatedPrice.toFixed(2)}€
          </p>
        </div>
      )}

      {/* Servings per unit */}
      <div className="space-y-2">
        <Label>Portions par pièce (optionnel)</Label>
        <div className="flex items-center gap-2">
          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={servingsMinInput}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '' || /^\d+$/.test(val)) {
                setServingsMinInput(val);
                updateServesPerUnit('min', val);
              }
            }}
            placeholder="Min"
            className="w-20"
          />
          <span className="text-gray-400">à</span>
          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={servingsMaxInput}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '' || /^\d+$/.test(val)) {
                setServingsMaxInput(val);
                updateServesPerUnit('max', val);
              }
            }}
            placeholder="Max"
            className="w-20"
          />
          <span className="text-gray-500 text-sm">personnes</span>
        </div>
        <p className="text-xs text-gray-500">
          Affiché au client : "Pour 3-4 personnes"
        </p>
      </div>

      {/* Disclaimer */}
      <div className="flex items-start gap-3 pt-2">
        <Checkbox
          id="disclaimer"
          checked={!!disclaimer}
          onCheckedChange={(checked) => {
            onDisclaimerChange(checked ? DEFAULT_DISCLAIMER : undefined);
          }}
        />
        <div className="space-y-1">
          <Label htmlFor="disclaimer" className="cursor-pointer font-normal">
            Afficher "{DEFAULT_DISCLAIMER}"
          </Label>
          <p className="text-xs text-gray-500">
            Ce message sera affiché sous le prix pour informer le client.
          </p>
        </div>
      </div>
    </div>
  );
}
